import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/atoms/checkbox'
import { TextareaField } from '@/components/molecules/textarea-field'

type Props = {
  visible: boolean
  onCopyReservation: () => void
  onUnlink?: () => void
  noticeMessage?: {
    value: string
    onChange: (message: string) => void
    onComplete: (memo: string) => void
  }
  frontSupported?: {
    value: boolean
    onChange: (value: boolean) => void
  }
}

export const ReservationSubMenu: React.FC<Props> = props => {
  const { t } = useTranslation()
  const [isCheckedNoticeMessage, setIsCheckedNoticeMessage] = useState<boolean>(!!props.noticeMessage?.value)

  const handleCheckboxChange = (checked: boolean) => {
    setIsCheckedNoticeMessage(checked)
  }

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    props.noticeMessage?.onChange(e.target.value)
  }

  const handleMessageBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    props.noticeMessage?.onComplete(e.target.value)
  }

  const handleFrontSupportedChange = (checked: boolean) => {
    props.frontSupported?.onChange(checked)
  }

  useEffect(() => {
    const isNeedClearMessage = props.noticeMessage?.value && !isCheckedNoticeMessage
    if (!props.visible && isNeedClearMessage) {
      props.noticeMessage!.onChange('')
      props.noticeMessage!.onComplete('')
    }
  }, [props.visible])

  return (
    <div css={containerStyle}>
      <div css={menuItemStyle} onClick={props.onCopyReservation}>
        <img alt="" css={iconStyle} src={require('@/static/images/icon_reservation_copy.svg')} />
        <span>{t('Copy reservation')}</span>
      </div>
      {props.onUnlink && (
        <div css={[menuItemStyle, unlinkMenuStyle]} onClick={props.onUnlink}>
          <img alt="" src={require('@/static/images/icon_delete2_warning.svg')} />
          <span>{t('ReservationSubMenu.Unlink')}</span>
        </div>
      )}
      {props.frontSupported && (
        <Checkbox
          value={props.frontSupported.value}
          onChange={handleFrontSupportedChange}
          label={t('Front desk available')}
          style={checkboxRowStyle}
        />
      )}
      {props.noticeMessage && (
        <div>
          <Checkbox
            value={isCheckedNoticeMessage}
            label={t('ReservationSubMenu.NoticeMessage')}
            onChange={handleCheckboxChange}
            style={checkboxRowStyle}
          />
          {isCheckedNoticeMessage && (
            <div css={textareaWrapperStyle}>
              <TextareaField
                name="noticeMessage"
                placeholder={t('ReservationSubMenu.NoticeMessagePlaceholder')}
                value={props.noticeMessage.value}
                handleChangeData={handleMessageChange}
                onBlur={handleMessageBlur}
                marginBottom={0}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const containerStyle = css({
  backgroundColor: '#fff',
  borderRadius: 5,
  boxShadow: '0px 3px 10px 0px rgba(0, 0, 0, 0.1)',
  width: 200,
})

const menuItemStyle = css({
  height: 34,
  cursor: 'pointer',
  padding: '0 16px',
  display: 'flex',
  alignItems: 'center',
  fontSize: 12,
  fontWeight: 'bold',
  color: '#676767',
  '&:hover': { backgroundColor: '#f2f2f2' },
})

const unlinkMenuStyle = css({
  color: '#F44D10',
  '>img': { marginLeft: -6 },
  '>span': { marginLeft: 2 },
})

const iconStyle = css({
  width: 16,
  height: 16,
  flexShrink: 0,
  marginRight: 9,
})

const checkboxRowStyle = css({
  height: 34,
  padding: '0 16px',
  '&:hover': { backgroundColor: '#f2f2f2' },
})

const textareaWrapperStyle = css({
  padding: '8px 16px 0',
  textarea: {
    padding: '12px',
    ':focus': { padding: '11px' },
  },
})
